import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { AuditTrail } from '@openx/types/auditTrail.ts';
import type { HistoryEntryDataGrouped, LabeledChange, SelectedChanges } from '@openx/utils/history/types.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CharactersCounter } from '../../../CharactersCounter/CharactersCounter.tsx';
import { CheckboxField } from '../../../CheckboxField/CheckboxField.tsx';
import { Drawer } from '../../../Drawer/Drawer.tsx';
import { type DrawerAction, DrawerActionBar } from '../../../Drawer/DrawerActionBar.tsx';
import { DrawerHeader } from '../../../Drawer/DrawerHeader.tsx';
import { TextField } from '../../../TextField/TextField.tsx';
import { shouldExcludeChangeFromRevert } from './utils.ts';

type HistoryEntryRevertDrawerProps = {
  historyEntriesData: HistoryEntryDataGrouped[];
  selectedAuditTrail?: AuditTrail;
  isOpen: boolean;
  onClose: () => void;
  onRevert?: (selectedChanges: SelectedChanges) => void;
};

export const HistoryEntryRevertDrawer = ({
  historyEntriesData,
  selectedAuditTrail,
  isOpen,
  onClose,
  onRevert,
}: HistoryEntryRevertDrawerProps) => {
  const { t } = useTranslation();
  const [selectedChanges, setSelectedChanges] = useState<SelectedChanges>({});
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedHistoryEntryData = useMemo(
    () => historyEntriesData.find(entry => entry.traceId === selectedAuditTrail?.trace_id),
    [selectedAuditTrail, historyEntriesData]
  );

  const allChangesFromSelectedEntry = useMemo(
    () => selectedHistoryEntryData?.what.flatMap(({ changes }) => changes) ?? [],
    [selectedHistoryEntryData]
  );

  const selectableChanges = useMemo(
    () =>
      allChangesFromSelectedEntry.filter(change => !!change.oldValue && !shouldExcludeChangeFromRevert(change.label)),
    [allChangesFromSelectedEntry]
  );

  const isAllSelected = useMemo(
    () => selectableChanges.length > 0 && selectableChanges.every(({ label }) => label in selectedChanges),
    [selectableChanges, selectedChanges]
  );

  const handleChangeToggle = useCallback((change: LabeledChange) => {
    setSelectedChanges((selectedChanges): SelectedChanges => {
      if (change.label in selectedChanges) {
        const { [change.label]: _, ...rest } = selectedChanges;
        return rest;
      }

      return { ...selectedChanges, [change.label]: change.oldValue } as SelectedChanges;
    });
  }, []);

  const handleSelectAllToggle = useCallback(() => {
    if (isAllSelected) {
      setSelectedChanges({});
    } else {
      setSelectedChanges(
        selectableChanges.reduce<SelectedChanges>((selectedChanges, change) => {
          selectedChanges[change.label] = change.oldValue!;
          return selectedChanges;
        }, {})
      );
    }
  }, [isAllSelected, selectableChanges]);

  const onFormSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onRevert?.({ ...selectedChanges, ...(!!description && { notes: description }) });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedChanges, description, onRevert]);

  const drawerActions: DrawerAction[] = useMemo(
    () => [
      {
        'data-test': 'revert-selected-changes-button',
        color: 'primary',
        disabled: Object.keys(selectedChanges).length === 0,
        label: t('Revert Selected Changes'),
        loading: isSubmitting,
        onClick: onFormSubmit,
        variant: 'contained',
      },
      {
        'data-test': 'revert-cancel-button',
        label: t('Cancel'),
        onClick: onClose,
        variant: 'text',
      },
    ],
    [t, onClose, onFormSubmit, selectedChanges, isSubmitting]
  );

  return (
    <Drawer open={isOpen} onClose={onClose}>
      <DrawerHeader onClose={onClose}>{t('Revert Changes')}</DrawerHeader>
      <Typography variant="body1" gutterBottom>
        {t('Select changes you want to undo:')}
      </Typography>

      <CheckboxField
        name="select-all"
        label={t('Select All')}
        data-test="select-all-checkbox"
        checked={isAllSelected}
        onChange={handleSelectAllToggle}
        sx={{ pr: 3 }}
      />

      {selectedHistoryEntryData?.what.map(({ changes }, id) => (
        <Box key={`history-entries-revert-${selectedAuditTrail?.trace_id}-${id}`} sx={{ mt: 1 }}>
          {changes.map((change, idx) => {
            return (
              <Box key={`history-entries-revert-${selectedAuditTrail?.trace_id}-${id}-${idx}`} sx={{ mb: 3 }}>
                <Stack
                  data-test={`change-${idx}-details`}
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'flex-start',
                  }}
                >
                  <CheckboxField
                    checked={change.label in selectedChanges}
                    data-test={`checkbox-for-${idx}-${change.label}`}
                    disabled={!change.oldValue || shouldExcludeChangeFromRevert(change.label)}
                    onChange={() => handleChangeToggle(change)}
                    name={change.label}
                  />
                  {change.OldValueElement}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ))}

      <TextField
        label={t('Description (Optional)')}
        fullWidth
        multiline
        rows={2}
        data-test="revert-description"
        value={description}
        onChange={e => setDescription(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 255 }, formHelperText: { component: 'div' } }}
        helperText={<CharactersCounter maxValue={255} currentValue={description.length} />}
        sx={{
          mt: 2,
        }}
      />

      <DrawerActionBar actions={drawerActions} gutterBottom stickyBottom />
    </Drawer>
  );
};
